import React, { useMemo, useRef, useState } from 'react';
import { DIAS_SEMANA, diaBR, diaIso, num, dec } from './comum';

// Graficos da Analise do Banco, seguindo o metodo da skill dataviz:
//  - partes de um todo (quem usa a CPU) -> barra segmentada, nao 4 cartoes;
//  - contagem ao longo do tempo -> barras por dia com eixo, uma serie com
//    enfase (o resto em cinza), rotulo direto so no pico;
//  - concentracao por horario -> grade dia da semana x hora, uma so cor
//    (sequencial: escuro = pouco, claro = muito, no tema escuro);
//  - todo grafico tem tooltip no hover.

// ── Barra segmentada ────────────────────────────────────────────────────────
export function BarraSegmentada({ partes, onClick, formatar = (p, t) => `${dec((100 * p.valor) / t, 1)}%` }) {
  const total = partes.reduce((s, p) => s + p.valor, 0) || 1;
  const [ativo, setAtivo] = useState(null);
  return (
    <div className="ab-seg">
      <div className="ab-seg__barra" role="img" aria-label={partes.map((p) => `${p.rotulo} ${formatar(p, total)}`).join(', ')}>
        {partes.map((p) => {
          const largura = (100 * p.valor) / total;
          return (
            <button key={p.chave} type="button" className="ab-seg__parte" style={{ width: `${largura}%`, background: p.cor, opacity: ativo && ativo !== p.chave ? 0.55 : 1 }}
              onMouseEnter={() => setAtivo(p.chave)} onMouseLeave={() => setAtivo(null)} onClick={() => onClick?.(p.chave)}
              title={`${p.rotulo}: ${formatar(p, total)}${p.detalhe ? ` · ${p.detalhe}` : ''} — clique para ver as consultas`}>
              {largura >= 9 && <span>{p.rotulo} {formatar(p, total)}</span>}
            </button>
          );
        })}
      </div>
      <div className="ab-legenda">
        {partes.map((p) => (
          <button type="button" key={p.chave} className="ab-legenda__item" onClick={() => onClick?.(p.chave)}>
            <i style={{ background: p.cor }} />{p.rotulo} <strong>{formatar(p, total)}</strong>
          </button>
        ))}
      </div>
    </div>
  );
}

// Escala "bonita" para o eixo: 0, metade, topo arredondado.
function topoBonito(max) {
  if (max <= 5) return 5;
  const p = 10 ** Math.floor(Math.log10(max));
  const m = max / p;
  const passo = m <= 1 ? 1 : m <= 2 ? 2 : m <= 2.5 ? 2.5 : m <= 5 ? 5 : 10;
  return passo * p;
}

// ── Barras por dia ──────────────────────────────────────────────────────────
// total:    { 'YYYY-MM-DD': n }  — tudo
// destaque: { 'YYYY-MM-DD': n }  — o problema selecionado (opcional)
export function BarrasDia({ total, destaque, dias = 90, onDia, corDestaque = 'var(--ab-erp)', rotuloSerie = 'deadlocks' }) {
  const ref = useRef(null);
  const [hover, setHover] = useState(null);
  const serie = useMemo(() => {
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    return Array.from({ length: dias }, (_, i) => {
      const d = new Date(hoje); d.setDate(d.getDate() - (dias - 1 - i));
      const k = diaIso(d);
      return { dia: k, n: total?.[k] || 0, d: destaque?.[k] || 0 };
    });
  }, [total, destaque, dias]);
  const max = topoBonito(Math.max(1, ...serie.map((s) => s.n)));
  const L = 780; const A = 170; const padE = 34; const padB = 22; const padT = 16;
  const larg = (L - padE) / serie.length;
  const y = (v) => padT + (A - padT - padB) * (1 - v / max);
  const pico = serie.reduce((a, b) => (b.n > a.n ? b : a), serie[0]);
  const sel = hover !== null ? serie[hover] : null;
  return (
    <div className="ab-graf" ref={ref}>
      <svg viewBox={`0 0 ${L} ${A}`} width="100%" role="img" aria-label={`${rotuloSerie} por dia, últimos ${dias} dias`} onMouseLeave={() => setHover(null)}>
        {[0, max / 2, max].map((v) => (
          <g key={v}>
            <line x1={padE} y1={y(v)} x2={L} y2={y(v)} stroke="var(--ab-grade)" strokeWidth="1" />
            <text x={padE - 6} y={y(v) + 3} textAnchor="end" className="ab-eixo">{num(v)}</text>
          </g>
        ))}
        {serie.map((s, i) => {
          const x = padE + i * larg;
          const hTotal = (A - padT - padB) * (s.n / max);
          const hDest = (A - padT - padB) * (s.d / max);
          return (
            <g key={s.dia} onMouseEnter={() => setHover(i)} onClick={() => s.n && onDia?.(s.dia)} style={{ cursor: s.n ? 'pointer' : 'default' }}>
              <rect x={x} y={padT} width={larg} height={A - padT - padB} fill="transparent" />
              {s.n > 0 && <rect x={x + 1} y={y(s.n)} width={Math.max(larg - 2, 1)} height={Math.max(hTotal, 1.5)} rx="2" fill={destaque ? 'var(--ab-cinza)' : corDestaque} opacity={hover === null || hover === i ? 1 : 0.6} />}
              {destaque && s.d > 0 && <rect x={x + 1} y={y(s.d)} width={Math.max(larg - 2, 1)} height={Math.max(hDest, 1.5)} rx="2" fill={corDestaque} />}
            </g>
          );
        })}
        {pico?.n > 0 && (
          <text x={padE + serie.indexOf(pico) * larg + larg / 2} y={y(pico.n) - 4} textAnchor="middle" className="ab-eixo ab-eixo--forte">
            {num(pico.n)} em {diaBR(pico.dia)}
          </text>
        )}
        {serie.map((s, i) => ((i % 14 === 0 && serie.length - 1 - i >= 7) || i === serie.length - 1 ? (
          <text key={`x${i}`} x={padE + i * larg + larg / 2} y={A - 6} textAnchor={i === serie.length - 1 ? 'end' : 'middle'} className="ab-eixo">{diaBR(s.dia)}</text>
        ) : null))}
      </svg>
      {sel && (
        <div className="ab-tooltip" style={{ left: `${(100 * (padE + hover * larg + larg / 2)) / L}%` }}>
          <strong>{diaBR(sel.dia)}</strong> · {num(sel.n)} {rotuloSerie}
          {destaque && <><br />deste problema: {num(sel.d)}</>}
          {sel.n > 0 && onDia && <><br /><span className="cell-muted">clique para ver o dia</span></>}
        </div>
      )}
    </div>
  );
}

// ── Grade dia da semana x hora ──────────────────────────────────────────────
// celulas: { 'diaSemana-hora': n } (diaSemana 0 = domingo)
const RAMPA = ['#0d366b', '#104281', '#184f95', '#1c5cab', '#256abf', '#2a78d6', '#3987e5', '#5598e7', '#6da7ec', '#86b6ef'];
export function GradeDiaHora({ celulas, rotuloSerie = 'deadlocks' }) {
  const [hover, setHover] = useState(null);
  const max = Math.max(1, ...Object.values(celulas || {}));
  const cor = (n) => (n ? RAMPA[Math.min(RAMPA.length - 1, Math.floor((RAMPA.length - 1) * Math.sqrt(n / max)))] : 'var(--ab-vazio)');
  const ordemDias = [1, 2, 3, 4, 5, 6, 0]; // segunda primeiro
  const total = Object.values(celulas || {}).reduce((s, n) => s + n, 0);
  return (
    <div className="ab-gradeh" onMouseLeave={() => setHover(null)}>
      <div className="ab-gradeh__corpo">
        <span />
        {Array.from({ length: 24 }, (_, h) => <span key={h} className="ab-gradeh__hora">{h % 3 === 0 ? `${h}h` : ''}</span>)}
        {ordemDias.map((d) => (
          <React.Fragment key={d}>
            <span className="ab-gradeh__dia">{DIAS_SEMANA[d]}</span>
            {Array.from({ length: 24 }, (_, h) => {
              const n = celulas?.[`${d}-${h}`] || 0;
              return (
                <span key={h} className="ab-gradeh__cel" style={{ background: cor(n), outline: hover && hover.d === d && hover.h === h ? '2px solid var(--portal-text)' : 'none' }}
                  onMouseEnter={() => setHover({ d, h, n })} />
              );
            })}
          </React.Fragment>
        ))}
      </div>
      <div className="ab-gradeh__rodape">
        {hover
          ? <span><strong>{DIAS_SEMANA[hover.d]} {hover.h}h</strong> · {num(hover.n)} {rotuloSerie}{total ? ` (${dec((100 * hover.n) / total, 0)}%)` : ''}</span>
          : <span className="cell-muted">passe o mouse · mais claro = mais {rotuloSerie}</span>}
        <span className="ab-gradeh__escala"><i style={{ background: RAMPA[0] }} /><i style={{ background: RAMPA[4] }} /><i style={{ background: RAMPA[9] }} /> pouco → muito</span>
      </div>
    </div>
  );
}

// Agrega linhas {diaSemana, hora, n} (do /resumo) em celulas.
export const celulasDe = (linhas) => (linhas || []).reduce((acc, l) => {
  const k = `${l.diaSemana}-${l.hora}`;
  acc[k] = (acc[k] || 0) + l.n;
  return acc;
}, {});
